import { Component, Prop, State, Host, Watch, h } from "@stencil/core";
import api from "../../utils/api";
import { ECatalogItem, ECatalogResponse } from "./e-catalog-response";
import row from "./row";

@Component({
    tag: "ux-iws-e-catalog",
    styleUrl: "e-catalog.scss",
})
export class ECatalog {
    private baseClass = "ux-iws-e-catalog";

    /** endpoint where data can be requested */
    @Prop() url: string = "";
    /** CSS selector for the table */
    @Prop() tableSelector: string = "";
    /** CSS selector for the element where the total number of eCatalogs should be placed */
    @Prop() totalNumberSelector: string = "";
    /** CSS selector for the button container below the page title */
    @Prop() buttonContainerSelector: string = "";
    /** CSS selector for the scroll-sensor */
    @Prop() scrollSensorSelector: string = "";
    /** debug */
    @Prop() debug: boolean = false;

    /** table element */
    private table: HTMLTableElement | null = null;
    /** table body element where the data should be put */
    private tableBody: HTMLTableSectionElement | null = null;
    /** NodeList of DOM elements where the total number of eCatalogs should be put */
    private totalNumberElements: NodeList | null = null;
    /** URL for getting the next page */
    private nextPage: string = "";
    /** DOM element where all the table rows should be put */
    private scrollSensor: HTMLUxInfiniteScrollSensorElement | null = null;
    /** date format to be used to format the dates */
    private dateFormat: string;
    /** the button container below the page title  */
    private buttonContainer: HTMLDivElement | null = null;
    /** the container of the button to be displayed when there are no results */
    private noResultsButtonContainer: HTMLDivElement;

    @State() isLoading: boolean = true;
    @State() hasData: boolean = false;
    @State() noDataHeadline: string = "";
    @State() noDataText: string = "";

    componentWillLoad() {
        this.setTable();
        this.setScrollSensor();
        this.setButtonContainer();
        this.setTotalNumberElements();
        this.requestData(this.url);
    }

    private _log = (...msg: any[]) => {
        if (this.debug && console && typeof console.log === "function") {
            console.log(`[E-Catalog]:`, ...msg);
        }
    };

    /** Connect total number */
    @Watch("totalNumberSelector")
    setTotalNumberElements() {
        if (this.totalNumberSelector === "") return;
        this.totalNumberElements = document.querySelectorAll(
            this.totalNumberSelector
        );
    }

    private updateNumbers(total: number): void {
        writeToElements(this.totalNumberElements, total.toString());
    }

    /** connect table */
    @Watch("tableSelector")
    setTable() {
        if (this.tableSelector === "") {
            this._log("Error: tableSelector is empty");
            return;
        }
        this.table = document.querySelector(
            this.tableSelector
        ) as HTMLTableElement;
        if (this.table) {
            this._log("Success: table has been set");
        } else {
            this._log("Error: table could not be set");
        }
        this.tableBody = this.table?.querySelector("tbody");
        if (this.tableBody) {
            this._log("Success: tableBody has been set");
        } else {
            this._log("Error: tableBody could not be set");
        }
    }

    @Watch("buttonContainerSelector")
    setButtonContainer() {
        if (this.buttonContainerSelector === "") {
            this._log("Error: buttonContainerSelector is empty");
            return;
        }
        this.buttonContainer = document.querySelector(
            this.buttonContainerSelector
        );
        if (this.buttonContainer) {
            this._log("Success: buttonContainer has been set");
        } else {
            this._log("Error: buttonContainer could not be set");
        }
    }

    /** connect scroll-sensor */
    @Watch("scrollSensorSelector")
    setScrollSensor() {
        if (this.scrollSensorSelector === "") {
            this._log("Error: scrollSensorSelector is empty");
            return;
        }
        this.scrollSensor = document.querySelector(
            this.scrollSensorSelector
        ) as HTMLUxInfiniteScrollSensorElement;
        if (this.scrollSensor !== null) {
            this.scrollSensor.addEventListener(
                "uxInfiniteScrollSensorScrolledIntoView",
                () => {
                    this.requestData(this.nextPage);
                }
            );
            this._log("Success: scrollSensor has been set");
        } else {
            this._log("Error: scrollSensor could not be set");
        }
    }

    /** post filter, date and sort settings to get data from the endpoint */
    @Watch("url")
    initialRequest() {
        this.requestData(this.url);
    }

    /** Send the real request and handle response */
    private async requestData(url: string) {
        if (url === "") return;
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "GET",
                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };
        const data = await api<ECatalogResponse>(url, options);
        if (data.items.length === 0 && this.hasData === true) {
            this._log(
                "Error: No data received in scroll-request because items length is 0"
            );
            return;
        }
        if (this.url === url) {
            if (data.items.length > 0) {
                // Only clear existing data if new items exist
                this.clearExistingData();
            }
        }
        // simulating calll time in dev environment
        if (isMockUrl(url))
            await new Promise((resolve) => setTimeout(resolve, 2000));

        if (data.items.length > 0) {
            this.hasData = true;
            this.isLoading = false;
            this._log("Success: Data received and loaded");
        } else {
            this.hasData = false;
            this.isLoading = false;
            this.noDataHeadline = data?.messages?.noDataHeadline;
            this.noDataText = data?.messages?.noDataText;
            this._log("Error: No data received because items length is 0");
        }
        this.dateFormat = data.timeFormat;
        if (data.buttonHTML) {
            this.appendCallToActionToPage(data.buttonHTML);
            this._log("Success: Button HTML appended to page");
        } else {
            this._log("Error: No button HTML to append to page");
        }
        this.appendItemsToTable(data.items);
        if (data.totalCount) this.updateNumbers(data.totalCount);
        this.nextPage = data.nextPage || "";
        if (this.nextPage !== "" && this.scrollSensor !== null) {
            this.scrollSensor.hasFired = false;
        }
    }

    private clearExistingData() {
        if (this.tableBody !== null) this.tableBody.textContent = "";
    }

    private appendCallToActionToPage = (buttonHTML: string) => {
        if (!this.hasData) {
            this.noResultsButtonContainer.innerHTML = buttonHTML;
            this._log("no-results button HTML has been set");
            this.buttonContainer.remove();
            this._log("No Data, therefore Button container has been removed");
        } else {
            this.buttonContainer.innerHTML = buttonHTML;
            this._log("Success: Button container HTML has been set");
        }
    };

    private appendItemsToTable(items: ECatalogItem[]) {
        if (this.tableBody === null) return;
        this.tableBody.insertAdjacentHTML(
            "beforeend",
            items.map(row(this.dateFormat)).join("")
        );
    }

    render() {
        const { baseClass, isLoading, hasData, noDataHeadline, noDataText } =
            this;

        return (
            <Host class={baseClass}>
                <div
                    class="ux-u-mt-xs"
                    style={{ display: isLoading ? "block" : "none" }}
                >
                    <ux-loader variant="spinner"></ux-loader>
                </div>
                <div
                    style={{
                        display: !isLoading && hasData ? "block" : "none",
                    }}
                >
                    <slot name="results"></slot>
                </div>
                <div
                    style={{
                        display: !hasData && !isLoading ? "block" : "none",
                    }}
                >
                    <div
                        class={`${baseClass}__no-results`}
                        data-testid="no-results"
                    >
                        <ux-icon name="documents-screen"></ux-icon>
                        <div class="ux-text-headline ux-text-headline--big ux-u-mt-xxxs">
                            {noDataHeadline}
                        </div>
                        <p class="ux-text-normal ux-text-normal--light-grey">
                            {noDataText}
                        </p>
                        <div
                            class={`${baseClass}__no-results-button-container`}
                            ref={(el) => (this.noResultsButtonContainer = el)}
                        ></div>
                    </div>
                </div>
            </Host>
        );
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}

function writeToElements(nodes: NodeList | null, text: string): void {
    if (nodes === null) return;
    [].forEach.call(
        nodes,
        (element: HTMLElement) => (element.textContent = text)
    );
}
